import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight, X, Sparkles } from 'lucide-react';
import api from '@/lib/api';

// One-time onboarding checklist shown on the Dashboard until every
// step is ticked (or the user dismisses it). Each step queries a
// cheap read endpoint on mount to decide whether it's already done.
// Dismissal is persisted to localStorage so returning users see the
// usual dashboard directly.

const DISMISS_KEY = 'brixlead.onboarding-dismissed';

interface Step {
  key: string;
  title: string;
  description: string;
  href: string;
  done: boolean;
}

export function OnboardingChecklist() {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(DISMISS_KEY); } catch { return false; }
  });

  useEffect(() => {
    if (dismissed) return;
    // Four low-cost endpoints in parallel. Any 4xx/5xx treated as
    // "step not done" — we'd rather over-show the checklist than miss
    // a real setup gap.
    const safeGet = (path: string, extractor: (r: any) => boolean): Promise<boolean> =>
      api.get(path).then((r) => {
        try { return extractor(r); } catch { return false; }
      }).catch(() => false);

    Promise.all([
      safeGet('/settings/integrations', (r) => !!(r.data?.email?.provider || r.data?.email?.emailProvider)),
      safeGet('/users', (r) => {
        const list = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.users) ? r.data.users : []);
        return list.length > 1;
      }),
      // Some list endpoints reject unknown params — avoid passing any.
      safeGet('/leads/stats', (r) => (r.data?.total ?? 0) > 0),
      safeGet('/workflows', (r) => Array.isArray(r.data) && r.data.some((w: any) => w?.isActive)),
    ]).then(([emailOk, usersOk, leadsOk, autoOk]) => {
      setSteps([
        {
          key: 'email',
          title: 'Connect email provider',
          description: 'So welcome emails and automations actually go out',
          href: '/settings',
          done: emailOk,
        },
        {
          key: 'users',
          title: 'Invite your team',
          description: 'Counselors, managers, admins',
          href: '/users',
          done: usersOk,
        },
        {
          key: 'leads',
          title: 'Add your first lead',
          description: 'Manual entry, CSV import, or connect a webform',
          href: '/leads',
          done: leadsOk,
        },
        {
          key: 'workflows',
          title: 'Set up a workflow',
          description: 'Auto WhatsApp / Email follow-up for clients and counselors',
          href: '/workflows',
          done: autoOk,
        },
      ]);
    }).catch(() => {
      // Never let this widget crash the Dashboard.
      setSteps(null);
    });
  }, [dismissed]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  // Render nothing while loading, after dismissal, or once all 4 steps done.
  if (dismissed || !steps) return null;
  const remaining = steps.filter((s) => !s.done).length;
  if (remaining === 0) return null;

  const pct = Math.round(((steps.length - remaining) / steps.length) * 100);

  return (
    <div className="relative bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-5">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
        title="Hide onboarding"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-[#6366F1]" />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900">Get set up in 10 minutes</p>
          <p className="text-[11px] text-slate-500">
            {steps.length - remaining} of {steps.length} done · {pct}%
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-white rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="space-y-2">
        {steps.map((s) => (
          <Link
            key={s.key}
            to={s.href}
            className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
              s.done ? 'bg-emerald-50/60 hover:bg-emerald-50' : 'bg-white hover:bg-indigo-50'
            }`}
          >
            {s.done ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-slate-300 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${s.done ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                {s.title}
              </p>
              <p className="text-[11px] text-slate-400">{s.description}</p>
            </div>
            {!s.done && <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />}
          </Link>
        ))}
      </div>
    </div>
  );
}
